import dayjs from 'dayjs';
import { useMemo } from 'react';
import { Badge, ProspectDetails, Typography, TypographyType } from '../../../../components';
import {
  DEFAULT_LANGUAGE,
  LANGUAGE_TO_FLAG,
  PROSPECT_CATEGORY_TO_COLOR,
  PROSPECT_TYPE_TO_LABEL,
} from '../../../../constants';
import { useAppSelector } from '../../../../hooks';
import { Alignment, DateFormat, Permissions, PracticeProspect, ProspectCategory, TextColor } from '../../../../types';
import { conditionalArray, snakeCaseToLabel } from '../../../../utils';
import SimulationDetailsActions from './SimulationDetailsActions';
import SimulationDetailsNotes from './SimulationDetailsNotes';
import SimulationDetailsPhoneNumber from './SimulationDetailsPhoneNumber';
import SimulationDetailsScorecard from './SimulationDetailsScorecard';
import SimulationDetailsSharedWith from './SimulationDetailsSharedWith';
import SimulationDetailsTags from './SimulationDetailsTags';

interface SimulationDetailsProps {
  prospect: PracticeProspect;
  runCall: () => void;
}

/**
 * Detailed view of a selected simulation.
 * Shows all simulation information and metadata.
 */
const SimulationDetails = ({ prospect, runCall }: SimulationDetailsProps) => {
  const isInterview = prospect.category === ProspectCategory.INTERVIEW;
  const isShareable = !isInterview;

  const permissions = useAppSelector((state) => state.auth.user?.permissions);
  const canManageSims = !!permissions?.includes(Permissions.MANAGE_PROSPECT);

  const sections = useMemo(
    () => [
      ...conditionalArray(canManageSims && isShareable, {
        title: 'Shared with',
        content: (
          <SimulationDetailsSharedWith
            personaId={prospect.personaId}
            currAllowedCallers={prospect.allowedCallers}
            currAllowedTeams={prospect.allowedTeams}
          />
        ),
      }),
      // Only show the language row if the sim is multilingual.
      ...conditionalArray(prospect.language !== DEFAULT_LANGUAGE, {
        title: 'Language',
        content: `${LANGUAGE_TO_FLAG[prospect.language]} ${snakeCaseToLabel(prospect.language)}`,
      }),
      // Only show the phone number row if the sim has a phone number assigned.
      // This is the only row that should be hidden if its value does not exist.
      // We're not using the conditionalArray util because its contents are still evaluated even if the condition is false,
      // causing a console error when the phone number does not exist.
      ...(prospect.associatedPhoneNumber
        ? [
            {
              title: 'Phone number',
              content: <SimulationDetailsPhoneNumber phoneNumber={prospect.associatedPhoneNumber} />,
            },
          ]
        : []),
      {
        title: 'Category',
        content: (
          <Badge color={PROSPECT_CATEGORY_TO_COLOR[prospect.category]} label={snakeCaseToLabel(prospect.category)} />
        ),
      },
      {
        title: 'Scenario',
        content: PROSPECT_TYPE_TO_LABEL[prospect.type],
      },
      {
        title: 'Last touchpoint',
        content: snakeCaseToLabel(prospect.lastTouchpoint),
      },
      ...conditionalArray(canManageSims, {
        title: 'AI scorecard',
        content: (
          <SimulationDetailsScorecard personaId={prospect.personaId} currTemplateId={prospect.scorecardTemplateId} />
        ),
      }),
      {
        title: 'Created',
        content: dayjs(prospect.createdAt).format(DateFormat.MONTH_DAY),
      },
      {
        title: 'Tags',
        content: (
          <SimulationDetailsTags
            isDraft={prospect.isDraft}
            personaId={prospect.personaId}
            prospectCategory={prospect.category}
            prospectUserId={prospect.userId}
            currTags={prospect.tags}
          />
        ),
      },
    ],
    [canManageSims, prospect, isShareable]
  );

  return (
    <div className="flex h-full w-full flex-col gap-6 overflow-y-auto p-4">
      <div className="flex flex-col gap-4">
        <ProspectDetails
          accountName={prospect.accountName}
          firstName={prospect.firstName}
          isFavorited={prospect.isFavorited}
          lastName={prospect.lastName}
          jobTitle={prospect.jobTitle}
          onFavoriteSuccess={() => {}}
          personaId={prospect.personaId}
          showAvatarWithFavorite
        />
        <SimulationDetailsActions prospect={prospect} runCall={runCall} />
      </div>
      <div className="flex w-full flex-1 flex-col gap-4">
        {sections.map((section) => (
          <div key={section.title} className="flex w-full items-start justify-between gap-2">
            <Typography className="min-w-24" color={TextColor.SECONDARY} type={TypographyType.X_SMALL}>
              {section.title}
            </Typography>
            {typeof section.content === 'string' && (
              <Typography alignment={Alignment.RIGHT} type={TypographyType.X_SMALL}>
                {section.content}
              </Typography>
            )}
            {typeof section.content !== 'string' && section.content}
          </div>
        ))}
        <SimulationDetailsNotes
          isDraft={prospect.isDraft}
          notes={prospect.notes || ''}
          personaId={prospect.personaId}
          prospectCategory={prospect.category}
          prospectUserId={prospect.userId}
        />
      </div>
    </div>
  );
};

export default SimulationDetails;
